import { useCallback, useMemo, useRef } from "react";

/**
 * 마이크 녹음(MediaRecorder) 얇은 감싸개.
 *
 * 음성인식(`useSpeechRecognition`)과 달리 소리를 **우리가 들고 있는다.** 브라우저
 * 지원도 훨씬 넓어서 앱 안의 브라우저에서도 대체로 된다.
 *
 * **`start()` 는 사용자가 누른 자리에서 부른다.** 마이크 권한이 사용자 동작에 묶여
 * 있다. 처음 한 번 물어보고 그 뒤에는 같은 스트림을 계속 쓴다(매번 새로 열면 iOS 가
 * 권한을 다시 묻고 소리도 난다).
 */

// 단말마다 되는 형식이 다르다. iOS 는 mp4, 안드로이드·데스크톱 크롬은 webm 이다.
const MIME_TYPE_CANDIDATE_LIST = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg;codecs=opus",
];

// 24kbps 로 시작했다가 아이 목소리가 뭉개져 들려서 올렸다. 학부모가 발음을
// 들어보는 것이 목적이라 알아들을 수 있는 정도로는 모자란다. 64kbps 는 초당 8KB 라
// 지문 하나가 1.8MB 남짓이고, 이 정도는 올리는 데 무리가 없다.
const SPEECH_BITS_PER_SECOND = 64000;

const pickMimeType = () => {
  if (typeof MediaRecorder === "undefined") return "";
  return (
    MIME_TYPE_CANDIDATE_LIST.find((candidate) =>
      MediaRecorder.isTypeSupported(candidate)
    ) || ""
  );
};

export const useAudioRecorder = () => {
  const streamRef = useRef(null);
  const recorderRef = useRef(null);

  const isSupported = useMemo(
    () =>
      typeof window !== "undefined" &&
      typeof MediaRecorder !== "undefined" &&
      Boolean(navigator.mediaDevices?.getUserMedia),
    []
  );

  const getStream = useCallback(async () => {
    if (streamRef.current) return streamRef.current;
    streamRef.current = await navigator.mediaDevices.getUserMedia({
      audio: {
        // 스피커로 나가는 TTS 가 마이크로 되돌아 들어오는 것은 막아야 한다.
        echoCancellation: true,
        // 잡음 제거와 자동 이득은 목소리까지 깎아 먹는다. 아이 발음을 들어보는 것이
        // 목적이라 원래 소리에 가까운 편이 낫다.
        noiseSuppression: false,
        autoGainControl: false,
        channelCount: 1,
      },
    });
    return streamRef.current;
  }, []);

  /** 녹음을 시작한다. 이미 돌고 있으면 그것을 버리고 새로 시작한다. */
  const startRecording = useCallback(async () => {
    if (!isSupported) return false;

    try {
      const stream = await getStream();
      if (recorderRef.current && recorderRef.current.state !== "inactive") {
        recorderRef.current.stop();
      }

      // 목소리만 담으므로 낮게 잡는다. 기본값(40~128kbps)으로 두면 파일이 서너 배가
      // 되는데, 나중에 학부모에게 보내려면 올리는 쪽이 학생 휴대폰이라 작아야 한다.
      // 24kbps opus 는 말소리에 충분하다.
      const recorder = new MediaRecorder(stream, {
        ...(pickMimeType() ? { mimeType: pickMimeType() } : {}),
        audioBitsPerSecond: SPEECH_BITS_PER_SECOND,
      });
      const chunkList = [];

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) chunkList.push(event.data);
      };
      recorder.chunkList = chunkList;

      recorderRef.current = recorder;
      recorder.start();
      return true;
    } catch (error) {
      return false;
    }
  }, [isSupported, getStream]);

  /** 녹음을 멈추고 방금 담은 소리를 돌려준다. 담은 것이 없으면 null. */
  const stopRecording = useCallback(
    () =>
      new Promise((resolve) => {
        const recorder = recorderRef.current;
        if (!recorder || recorder.state === "inactive") {
          resolve(null);
          return;
        }

        recorder.onstop = () => {
          const chunkList = recorder.chunkList || [];
          resolve(
            chunkList.length
              ? new Blob(chunkList, { type: recorder.mimeType || "audio/webm" })
              : null
          );
        };
        recorder.stop();
      }),
    []
  );

  /** 화면을 벗어날 때. 마이크 표시가 계속 켜져 있으면 학생이 불안해한다. */
  const releaseMicrophone = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try {
        recorderRef.current.stop();
      } catch (error) {
        // 이미 멈춘 뒤라면 할 일이 없다.
      }
    }
    recorderRef.current = null;

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  /**
   * 한 번 읽는 것을 담는다. **말이 시작된 뒤 잠깐 조용해지면 알아서 끝난다.**
   *
   * 같은 마이크 스트림에 분석기(AnalyserNode)를 물려 음량을 재므로 마이크를 두 번
   * 열지 않는다(두 번 열면 iOS 에서 다른 쪽이 죽는다). 음량은 `onLevel` 로 화면에
   * 흘려 준다. 음파 표시가 이것으로 움직인다.
   *
   * 끝나는 조건: 말 시작 후 1.4초 무음, `finishCapture()` 호출, 또는 20초 상한.
   */
  const captureFinishRef = useRef(null);

  const finishCapture = useCallback(() => {
    if (captureFinishRef.current) captureFinishRef.current();
  }, []);

  const captureRead = useCallback(
    async ({ onLevel = null } = {}) => {
      if (!isSupported) return null;

      const SILENCE_AFTER_SPEECH_MILLISECONDS = 1400;
      const MAXIMUM_CAPTURE_MILLISECONDS = 20000;
      // 이 아래는 무음으로 본다. 교실 소음보다 높고 또렷한 말소리보다 낮게 잡았다.
      const SPEECH_LEVEL_THRESHOLD = 0.02;

      const isStarted = await startRecording();
      if (!isStarted) return null;

      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      const audioContext = new AudioContextClass();
      // iOS 는 사용자 동작 밖에서 만든 컨텍스트를 잠가 둔다.
      if (audioContext.state === "suspended") await audioContext.resume();

      const source = audioContext.createMediaStreamSource(streamRef.current);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      const sampleBuffer = new Float32Array(analyser.fftSize);

      let hasSpeechStarted = false;
      let silentMilliseconds = 0;
      let isFinished = false;

      await new Promise((resolve) => {
        captureFinishRef.current = () => {
          isFinished = true;
          resolve();
        };

        const startedAt = performance.now();
        const tick = () => {
          if (isFinished) return;

          analyser.getFloatTimeDomainData(sampleBuffer);
          let sum = 0;
          for (let i = 0; i < sampleBuffer.length; i++) {
            sum += sampleBuffer[i] * sampleBuffer[i];
          }
          const level = Math.sqrt(sum / sampleBuffer.length);
          if (onLevel) onLevel(level);

          if (level >= SPEECH_LEVEL_THRESHOLD) {
            hasSpeechStarted = true;
            silentMilliseconds = 0;
          } else if (hasSpeechStarted) {
            silentMilliseconds += 100;
          }

          const elapsed = performance.now() - startedAt;
          if (
            (hasSpeechStarted &&
              silentMilliseconds >= SILENCE_AFTER_SPEECH_MILLISECONDS) ||
            elapsed >= MAXIMUM_CAPTURE_MILLISECONDS
          ) {
            isFinished = true;
            resolve();
            return;
          }

          setTimeout(tick, 100);
        };
        tick();
      });

      captureFinishRef.current = null;
      source.disconnect();
      if (audioContext.state !== "closed") audioContext.close();
      if (onLevel) onLevel(0);

      return stopRecording();
    },
    [isSupported, startRecording, stopRecording]
  );

  return {
    isSupported,
    startRecording,
    stopRecording,
    captureRead,
    finishCapture,
    releaseMicrophone,
  };
};

// ── 이어 붙이기 ───────────────────────────────────────────────────────────
//
// 녹음 조각을 그냥 이어 붙이면 안 된다. 조각마다 제 머리말을 달고 있어서 첫 조각만
// 재생되고 나머지는 잡음이 되거나 아예 안 열린다. 그래서 소리로 되돌린 뒤(디코드)
// 하나로 잇고 WAV 로 다시 굽는다. WAV 는 머리말이 단순해 손으로 만들 수 있고
// 어느 브라우저에서나 열린다.

// 16kHz 는 전화 수준이라 치찰음(s, th)이 뭉개진다. 발음을 들어보는 것이 목적이므로
// 24kHz 로 올렸다. 44.1kHz 까지 갈 필요는 없다.
const OUTPUT_SAMPLE_RATE = 24000;
// 조각 사이에 두는 숨. 붙여 놓으면 문장이 뭉개져 들린다.
const GAP_SECONDS = 0.35;

const encodeWav = (sampleList, sampleRate) => {
  const buffer = new ArrayBuffer(44 + sampleList.length * 2);
  const view = new DataView(buffer);

  const writeText = (offset, text) => {
    for (let i = 0; i < text.length; i++) {
      view.setUint8(offset + i, text.charCodeAt(i));
    }
  };

  writeText(0, "RIFF");
  view.setUint32(4, 36 + sampleList.length * 2, true);
  writeText(8, "WAVE");
  writeText(12, "fmt ");
  view.setUint32(16, 16, true); // fmt 길이
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // 모노
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // 초당 바이트
  view.setUint16(32, 2, true); // 한 표본의 바이트
  view.setUint16(34, 16, true); // 비트 깊이
  writeText(36, "data");
  view.setUint32(40, sampleList.length * 2, true);

  for (let i = 0; i < sampleList.length; i++) {
    const clamped = Math.max(-1, Math.min(1, sampleList[i]));
    view.setInt16(44 + i * 2, clamped * 0x7fff, true);
  }

  return new Blob([buffer], { type: "audio/wav" });
};

/** 녹음 조각들을 순서대로 이어 붙인 WAV 하나를 만든다. 없으면 null. */
export const buildCombinedRecording = async (blobList) => {
  const usableList = (blobList || []).filter(Boolean);
  if (usableList.length === 0) return null;

  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;

  const audioContext = new AudioContextClass({
    sampleRate: OUTPUT_SAMPLE_RATE,
  });

  try {
    const bufferList = [];
    for (const blob of usableList) {
      try {
        const arrayBuffer = await blob.arrayBuffer();
        bufferList.push(await audioContext.decodeAudioData(arrayBuffer));
      } catch (error) {
        // 못 읽는 조각은 건너뛴다. 하나 때문에 전체를 잃지 않는다.
      }
    }

    if (bufferList.length === 0) return null;

    const sampleRate = bufferList[0].sampleRate;
    const gapLength = Math.round(GAP_SECONDS * sampleRate);
    const totalLength = bufferList.reduce(
      (sum, buffer) => sum + buffer.length + gapLength,
      0
    );

    const merged = new Float32Array(totalLength);
    let offset = 0;
    for (const buffer of bufferList) {
      merged.set(buffer.getChannelData(0), offset);
      offset += buffer.length + gapLength;
    }

    return encodeWav(merged, sampleRate);
  } finally {
    if (audioContext.state !== "closed") audioContext.close();
  }
};
